import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import React, { useCallback, useContext, useRef } from 'react'
import NavBarButton from '../NavBarButton/NavBarButton'
import ContactContext from 'contexts/contactContext'
import useToggle from 'hooks/useToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import NavBarContactsItems from './NavBarContactsItems'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import ModalContext from 'contexts/ModalContext'
import Modal from 'LuxKit/Modal/Modal'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import GeoContext from 'contexts/geoContext'
import useOrders from 'hooks/Order/useOrders'
import { useAppSelector } from 'hooks/reduxHooks'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'

interface Props {
  className?: string
  /**
   * The minimum breakpoint when the text disappears
   */
  textBreakpoint?: App.ScreenSize
}

function NavBarContactsButton(props: Props) {
  const { className, textBreakpoint } = props
  const [open, toggleOpen, _, closeDropdown] = useToggle()

  const { currentRegionCode } = useContext(GeoContext)
  const chatEnabled = useAppSelector(isSupportAssistantEnabled)
  const [upcomingOrders] = useOrders('upcoming')
  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const contact = useContext(ContactContext)

  const onNavClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    if (isMobile) {
      showModal(resolve => <Modal title="Contact us 24/7">
        <NavBarContactsItems onItemClick={resolve} />
      </Modal>)
    } else {
      toggleOpen()
    }
  }, [isMobile, showModal, toggleOpen])

  const has247Support = !NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)

  if (upcomingOrders.length && chatEnabled) {
    // Push the user to use our chat if they have an upcoming order instead of calling us
    return null
  }

  return <>
    <Group direction="horizontal" gap={4} verticalAlign="center" className={className}>
      <CSSBreakpoint min={textBreakpoint}>
        <CSSBreakpoint max="tablet">
          <BodyText variant="medium" wrap="no-wrap">Contact us</BodyText>
        </CSSBreakpoint>
        <CSSBreakpoint min="desktop">
          <Caption variant="medium" wrap="no-wrap">
            {!has247Support && 'Contact us 7 days'}
            {has247Support && 'Contact us 24 hours, 7 days'}
          </Caption>
        </CSSBreakpoint>
      </CSSBreakpoint>
      <NavBarButton
        size="small"
        icon={<LinePhoneIcon />}
        dropdown
        ref={buttonRef}
        onClick={onNavClick}
      >
        <CSSBreakpoint as="span" max="tablet">Call</CSSBreakpoint>
        <CSSBreakpoint as="span" min="desktop">{contact.defaultContact.humanReadable}</CSSBreakpoint>
      </NavBarButton>
    </Group>
    <DropdownList
      size="S"
      open={!isMobile && open}
      onClose={closeDropdown}
      anchorRef={buttonRef}
      triggerRef={buttonRef}
    >
      <NavBarContactsItems />
    </DropdownList>
  </>
}

export default React.memo(NavBarContactsButton)
